<template>
    <div>
        <div class="mb-2 flex items-center justify-end">
            <button
                @click="fetchPaymentMethods"
                :disabled="loading"
                class="flex items-center space-x-1 text-xs text-indigo-400 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 disabled:opacity-50 transition-colors"
            >
                <ArrowPathIcon :class="{ 'animate-spin': loading }" class="h-3 w-3" />
                <span>{{ loading ? 'Carregando...' : 'Atualizar' }}</span>
            </button>
        </div>
        
        <ticto-loading v-if="loading" />
        
        <div v-else-if="paymentMethods.length === 0" class="text-center py-4 text-gray-500 dark:text-gray-400">
            Nenhum método de pagamento encontrado
        </div>
        
        <div v-else class="space-y-2">
            <div 
                v-for="(method, index) in paymentMethods" 
                :key="method.payment_method"
                class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg transition-colors"
                :class="getMethodBorderColor(method, index)"
            >
                <div class="flex items-center space-x-3">
                    <div class="flex flex-col items-center">
                        <div class="text-xs font-bold text-gray-500 dark:text-gray-400">#{{ index + 1 }}</div>
                        <div class="h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold"
                             :class="getMethodRankColor(index)">
                            {{ index + 1 }}
                        </div>
                    </div>
                    <div class="flex flex-col">
                        <div class="flex items-center space-x-2">
                            <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {{ formatPaymentMethod(method.payment_method) }}
                            </span>
                            <component 
                                :is="getPaymentMethodIcon(method.payment_method)" 
                                class="h-4 w-4 text-gray-500 dark:text-gray-400" 
                            />
                            <span v-if="method.percentage > 70" 
                                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border dark:border-yellow-500/30">
                                <ExclamationTriangleIcon class="h-3 w-3 mr-1" />
                                Concentração Alta
                            </span>
                            <span v-else-if="method.percentage > 50" 
                                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 dark:border dark:border-blue-500/30">
                                Concentração Média
                            </span>
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                            {{ method.total }} transações
                        </div>
                    </div>
                </div>
                
                <div class="flex items-center space-x-4">
                    <div class="text-right">
                        <div class="text-xs font-medium" :class="getPercentageColor(method.percentage)">
                            {{ method.percentage }}% do total
                        </div>
                    </div>
                    <div class="text-sm font-medium text-green-600 dark:text-green-400">
                        {{ $root.formatCurrency(method.amount) }}
                    </div>
                </div>
            </div>
        </div>
        
        <div v-if="paymentMethods.length > 0" class="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div class="text-xs text-gray-500 dark:text-gray-400 text-center">
                Total de {{ paymentMethods.length }} métodos de pagamento
            </div>
        </div>
    </div>
</template>

<script>
import { 
    CreditCardIcon, 
    ArrowPathIcon, 
    ExclamationTriangleIcon,
    BanknotesIcon,
    DevicePhoneMobileIcon
} from '@heroicons/vue/24/outline'
import TictoLoading from '../../layouts/TictoLoading.vue';

export default {
    components: {
        CreditCardIcon,
        ArrowPathIcon,
        ExclamationTriangleIcon,
        BanknotesIcon,
        DevicePhoneMobileIcon,
        TictoLoading
    },
    name: 'PaymentMethodCard',
    props: {
        cashout: {
            type: Object,
            required: true
        }
    },
    
    data() {
        return {
            loading: false,
            paymentMethods: []
        };
    },
    
    mounted() {
        // Garante que os dados sejam carregados imediatamente para mostrar o resumo
        this.$nextTick(() => {
            this.fetchPaymentMethods();
        });
    },
    
    methods: {
        async fetchPaymentMethods() {
            if (!this.cashout?.id) return;
            
            this.loading = true;
            
            try {
                const { data } = await axios.get(`/api/cashout/${this.cashout.id}/review/payment-methods`);
                this.paymentMethods = data.data || [];
            } catch (error) {
                console.error('Erro ao buscar métodos de pagamento:', error);
                this.paymentMethods = [];
            } finally {
                this.loading = false;
                // Sempre emite os dados ao final, para garantir que o resumo seja atualizado
                this.$emit('data-loaded', this.paymentMethods);
            }
        },
        
        formatPaymentMethod(method) {
            const methods = {
                'credit_card': 'Cartão de Crédito',
                'debit_card': 'Cartão de Débito',
                'pix': 'PIX',
                'pix_recurring': 'PIX Recorrente',
                'boleto': 'Boleto',
                'bank_slip': 'Boleto Bancário',
                'transfer': 'Transferência'
            };

            return methods[method] || method.replace('_', ' ').toUpperCase();
        },
        
        getPaymentMethodIcon(method) {
            const icons = {
                'credit_card': 'CreditCardIcon',
                'debit_card': 'CreditCardIcon',
                'pix': 'DevicePhoneMobileIcon',
                'pix_recurring': 'DevicePhoneMobileIcon',
                'boleto': 'BanknotesIcon',
                'bank_slip': 'BanknotesIcon',
                'transfer': 'BanknotesIcon'
            };

            return icons[method] || 'CreditCardIcon';
        },
        
        getMethodRankColor(index) {
            if (index === 0) return 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800/30 dark:text-yellow-300 dark:border dark:border-yellow-500/50'; // 1º lugar - dourado
            if (index === 1) return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300 dark:border dark:border-gray-500/50';     // 2º lugar - prata
            if (index === 2) return 'bg-orange-200 text-orange-800 dark:bg-orange-800/30 dark:text-orange-300 dark:border dark:border-orange-500/50'; // 3º lugar - bronze
            return 'bg-blue-100 text-blue-800 dark:bg-blue-800/30 dark:text-blue-300 dark:border dark:border-blue-500/50';                      // demais
        },
        
        getMethodBorderColor(method, index) {
            if (method.percentage > 70) return 'border-red-500 dark:border-red-400';
            if (method.percentage > 50) return 'border-yellow-500 dark:border-yellow-400';
            if (index === 0) return 'border-yellow-400 dark:border-yellow-500';
            return 'border-blue-400 dark:border-blue-500';
        },
        
        getPercentageColor(percentage) {
            if (percentage > 70) return 'text-red-600 dark:text-red-400';
            if (percentage > 50) return 'text-yellow-600 dark:text-yellow-400';
            return 'text-gray-600 dark:text-gray-400';
        }
    }
};
</script> 